// Pattern Sets: every card face up. Find a group where each feature is all the same
// or all different. Groups of three at first; groups of four at the top rungs.
// From the notes: "not traditional match; all revealed; 3x3 to 9x9".

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import type { GameModule, GameContext } from '@shell/types';
import { specFor, deal, isGroup, brokenAttrs, findGroups, COLORS, type Card, type BoardSpec } from '@problems/pattern-sets';

const ATTR_LABEL = ['color', 'shape', 'fill', 'count'];
const WORD = ['', '', 'two', 'three', 'four'];

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const spec: BoardSpec = specFor(level);
  const K = spec.k;
  let { board, deck } = deal(spec, ctx.rng) as { board: (Card | null)[]; deck: Card[] };
  let sel: number[] = [];
  let found = 0;
  let attempts = 0, correct = 0;
  let flash: { idx: number[]; good: boolean; until: number } | null = null;
  let hintIdx: number | null = null;
  let hinted = false;
  let ended = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Groups found: ', h('strong', null, '0'), ` of ${spec.setsToFind}`), h('button.btn.small.ghost', { onclick: hint }, 'Hint'));
  const stage = h('div');
  const rule = h('p.center.muted.small', null, `Pick ${WORD[K]} cards. Each feature must be all the same or all different: ${ATTR_LABEL.slice(0, spec.attrs).join(', ')}.`);
  host.append(head, stage, rule);

  const S = makeSurface(stage, { aspect: spec.rows / spec.cols * 1.0 + 0.02, onResize: draw });

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (ended) return;
      const cw = S.w / spec.cols, ch = S.h / spec.rows;
      const cx = Math.floor(p.x / cw), cy = Math.floor(p.y / ch);
      const i = cy * spec.cols + cx;
      if (i < 0 || i >= board.length || !board[i]) return;
      if (sel.includes(i)) sel = sel.filter(x => x !== i); else sel.push(i);
      ctx.sound('tick');
      if (sel.length === K) check();
      draw();
    },
  });

  function check() {
    const cards = sel.map(i => board[i]!);
    const ms = performance.now() - tProblem;
    const ok = isGroup(cards, K);
    attempts++;
    if (ok) {
      correct++; found++;
      ctx.report({ skill: 'logic', level, correct: true, hintUsed: hinted, ms, problemId: `sets:${spec.attrs}x${K}x${spec.cols}` });
      ctx.sound('ding');
      ctx.say(['A group!', 'Yes, that is a group!', 'Sharp eyes!'][found % 3]);
      flash = { idx: sel.slice(), good: true, until: performance.now() + 500 };
      const picked = sel.slice(); sel = []; hintIdx = null; hinted = false; tProblem = performance.now();
      (head.querySelector('strong') as HTMLElement).textContent = String(found);
      if (found >= spec.setsToFind) {
        ended = true;
        if (K === 4) ctx.charm('charm.sets4');
        setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 800);
      } else {
        setTimeout(() => replace(picked), 450);
      }
    } else {
      ctx.report({ skill: 'logic', level, correct: false, hintUsed: hinted, ms, problemId: `sets:${spec.attrs}x${K}x${spec.cols}` });
      ctx.sound('buzz');
      const broken = brokenAttrs(cards, K);
      const name = ATTR_LABEL[broken[0]];
      ctx.say(`Not quite. Look at ${name}: some match and some are different. Make all ${WORD[K]} the same, or all different.`);
      flash = { idx: sel.slice(), good: false, until: performance.now() + 500 };
      sel = [];
    }
    draw();
  }

  function replace(idx: number[]) {
    for (const i of idx) board[i] = deck.length ? deck.shift()! : null;
    // guarantee a group remains while cards are left
    let guard = 0;
    while (findGroups(board, K, 1).length === 0 && deck.length && guard++ < 60) {
      const slot = board.findIndex(c => c === null);
      const swapAt = slot >= 0 ? slot : Math.floor(ctx.rng() * board.length);
      const old = board[swapAt];
      board[swapAt] = deck.shift()!;
      if (old) deck.push(old);
    }
    if (findGroups(board, K, 1).length === 0) {
      ended = true;
      ctx.say('No groups are left. Round complete!');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 700);
    }
    draw();
  }

  function hint() {
    if (ended) return;
    const groups = findGroups(board, K, 1);
    if (!groups.length) return;
    hinted = true;
    hintIdx = groups[0][0];
    ctx.say(`One card of a group is glowing. Find its ${K - 1} friends.`);
    draw();
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.clearRect(0, 0, W, H);
    const cw = W / spec.cols, chh = H / spec.rows;
    const pad = Math.max(2, cw * 0.06);
    const now = performance.now();
    if (flash && now > flash.until) flash = null;
    board.forEach((card, i) => {
      const x = (i % spec.cols) * cw + pad, y = Math.floor(i / spec.cols) * chh + pad;
      const w = cw - pad * 2, hgt = chh - pad * 2;
      roundRect(c, x, y, w, hgt, Math.min(10, w * 0.15));
      c.fillStyle = '#fff'; c.fill();
      if (!card) return;
      const inFlash = flash && flash.idx.includes(i);
      c.lineWidth = sel.includes(i) || inFlash || hintIdx === i ? Math.max(2, w * 0.06) : 1.5;
      c.strokeStyle = inFlash ? (flash!.good ? '#5fbf6a' : '#e2574c') : sel.includes(i) ? '#3bb3a9' : hintIdx === i ? '#f5c542' : '#d8d2c2';
      c.stroke();
      drawCard(c, card, x, y, w, hgt, spec.attrs);
    });
    if (flash) requestAnimationFrame(draw);
  }

  function shapePath(c: CanvasRenderingContext2D, shape: number, px: number, py: number, r: number) {
    c.beginPath();
    if (shape === 0) c.arc(px, py, r, 0, Math.PI * 2);
    else if (shape === 1) c.rect(px - r, py - r, r * 2, r * 2);
    else if (shape === 2) { c.moveTo(px, py - r * 1.1); c.lineTo(px + r * 1.1, py + r * 0.9); c.lineTo(px - r * 1.1, py + r * 0.9); c.closePath(); }
    else { c.moveTo(px, py - r * 1.15); c.lineTo(px + r * 1.15, py); c.lineTo(px, py + r * 1.15); c.lineTo(px - r * 1.15, py); c.closePath(); }
  }

  function drawCard(c: CanvasRenderingContext2D, card: Card, x: number, y: number, w: number, hgt: number, attrs: number) {
    const color = COLORS[card[0]];
    const shape = card[1];
    const fill = attrs >= 3 ? card[2] : 0;
    const count = attrs >= 4 ? card[3] + 1 : 1;
    const cx = x + w / 2, cy = y + hgt / 2;
    const base = Math.min(w, hgt);
    let r: number, positions: [number, number][];
    if (count === 1) { r = base * 0.3; positions = [[cx, cy]]; }
    else if (count === 2) { r = base * 0.22; positions = [[cx, cy - r * 1.2], [cx, cy + r * 1.2]]; }
    else if (count === 3) { r = base * 0.17; positions = [[cx, cy - r * 2.3], [cx, cy], [cx, cy + r * 2.3]]; }
    else { r = base * 0.17; positions = [[cx - r * 1.3, cy - r * 1.3], [cx + r * 1.3, cy - r * 1.3], [cx - r * 1.3, cy + r * 1.3], [cx + r * 1.3, cy + r * 1.3]]; }
    for (const [px, py] of positions) {
      shapePath(c, shape, px, py, r);
      c.lineWidth = Math.max(2, r * 0.22);
      c.strokeStyle = color;
      if (fill === 0) { c.fillStyle = color; c.fill(); }
      else if (fill === 1 || fill === 3) {
        c.save(); c.clip();
        if (fill === 1) {
          c.strokeStyle = color; c.lineWidth = Math.max(1.2, r * 0.14);
          for (let sx = px - r * 1.2; sx <= px + r * 1.2; sx += Math.max(3, r * 0.35)) { c.beginPath(); c.moveTo(sx, py - r * 1.3); c.lineTo(sx, py + r * 1.3); c.stroke(); }
        } else {
          c.fillStyle = color;
          const step = Math.max(3.5, r * 0.5);
          for (let dx = -r * 1.1; dx <= r * 1.1; dx += step) for (let dy = -r * 1.1; dy <= r * 1.1; dy += step) { c.beginPath(); c.arc(px + dx, py + dy, Math.max(1, r * 0.12), 0, 7); c.fill(); }
        }
        c.restore();
        shapePath(c, shape, px, py, r);
        c.lineWidth = Math.max(2, r * 0.22); c.stroke();
      } else { c.stroke(); }
    }
  }

  draw();
  return { destroy() { unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.pattern-sets',
  title: 'Pattern Sets',
  blurb: 'All cards face up. Find a group that is all the same or all different in every way.',
  category: 'math',
  skills: ['logic'],
  levelRange: [1, 10],
  unlock: 'always',
  mount,
};
export default mod;
